/* ============================================================
   Cardio Online - Request a Quote

   Drives every host of snippets/co-quote-form.liquid:
     - the PDP right-rail accordion
     - the site-wide modal (footer + Help mega menu links)
     - the /pages/request-a-quote page

   SUBMISSION IS NATIVE, NOT AJAX, AND THAT IS DELIBERATE.
   Shopify's spam protection is switched on for contact forms. It injects a
   CAPTCHA token into native form submissions only, so a custom fetch to
   /contact is rejected with "400 Missing CAPTCHA token". Verified 2026-08-22
   against both multipart and urlencoded bodies. Do not "improve" this back
   into a fetch unless spam protection is turned off, which is a bad trade.

   So the form posts normally, Shopify redirects back to the same page with
   ?contact_posted=true, and co-quote-form.liquid renders its success block
   from form.posted_successfully?. This file's job around that is to:
     1. stash the field values before the page unloads, and
     2. fire the Klaviyo event on the way back in, then reopen whichever host
        the customer submitted from so they land on the confirmation.
   ============================================================ */
(function () {
  'use strict';

  var QUOTE_PATH = '/pages/request-a-quote';
  var STASH_KEY = 'coqr:pending';
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  /* ---------------------------------------------------------
     Conditional installation fields
     --------------------------------------------------------- */
  function wireInstall(root) {
    var panel = root.querySelector('[data-coqr-install-panel]');
    if (!panel) return;
    var radios = root.querySelectorAll('[data-coqr-install]');
    if (!radios.length) return;

    function sync() {
      var yes = root.querySelector('[data-coqr-install="yes"]');
      var on = !!(yes && yes.checked);
      panel.hidden = !on;
      // Only require the detail fields once an install quote is actually wanted,
      // otherwise a hidden required field blocks submit with no visible cause.
      Array.prototype.forEach.call(panel.querySelectorAll('[data-coqr-install-field]'), function (f) {
        var optional = f.hasAttribute('data-coqr-install-optional');
        if (on && !optional) {
          f.setAttribute('required', 'required');
        } else {
          f.removeAttribute('required');
          if (!on) f.value = '';
        }
      });
    }

    Array.prototype.forEach.call(radios, function (r) {
      r.addEventListener('change', sync);
    });
    sync();
  }

  /* ---------------------------------------------------------
     Touched state, so untouched required fields are not red
     --------------------------------------------------------- */
  function wireTouched(root) {
    Array.prototype.forEach.call(root.querySelectorAll('input,select,textarea'), function (f) {
      f.addEventListener('blur', function () { f.classList.add('is-touched'); });
    });
  }

  /* ---------------------------------------------------------
     Reading the form
     --------------------------------------------------------- */
  function readForm(form) {
    function val(name) {
      var el = form.querySelector('[name="contact[' + name + ']"]');
      return el ? (el.value || '').trim() : '';
    }
    var installEl = form.querySelector('[name="contact[Installation quote]"]:checked');
    return {
      email: val('email'),
      firstName: val('First name'),
      lastName: val('Last name'),
      phone: val('Phone'),
      address1: val('Address line 1'),
      address2: val('Address line 2'),
      suburb: val('Suburb'),
      state: val('State'),
      postcode: val('Postcode'),
      product: val('Product'),
      sku: val('SKU'),
      productUrl: val('Product URL'),
      quantity: val('Quantity'),
      install: installEl ? installEl.value : '',
      installLocation: val('Install location'),
      steps: val('Steps or stairs'),
      driveway: val('Driveway and parking access'),
      accessNotes: val('Access notes'),
      body: val('body'),
      source: val('Submitted from')
    };
  }

  /* ---------------------------------------------------------
     Klaviyo. identify() creates or updates the profile; it does NOT
     grant marketing consent, which is deliberate. Build the
     acknowledgement flow on the "Requested Quote" metric.
     --------------------------------------------------------- */
  function trackKlaviyo(data) {
    try {
      if (!data || !data.email) return;
      var k = window.klaviyo || (window._learnq = window._learnq || []);

      var profile = { $email: data.email, $country: 'Australia' };
      if (data.firstName) profile.$first_name = data.firstName;
      if (data.lastName) profile.$last_name = data.lastName;
      if (data.phone) profile.$phone_number = data.phone;
      if (data.address1) profile.$address1 = data.address1;
      if (data.address2) profile.$address2 = data.address2;
      if (data.suburb) profile.$city = data.suburb;
      if (data.state) profile.$region = data.state;
      if (data.postcode) profile.$zip = data.postcode;

      var props = {
        // Full delivery address rides on the event as well as the profile, so an
        // internal alert can quote freight without opening Klaviyo.
        'Address line 1': data.address1 || '',
        'Address line 2': data.address2 || '',
        Suburb: data.suburb || '',
        State: data.state || '',
        Postcode: data.postcode || '',
        Product: data.product || '',
        SKU: data.sku || '',
        'Product URL': data.productUrl || '',
        Quantity: data.quantity || '',
        'Installation quote': data.install || '',
        'Install location': data.installLocation || '',
        'Steps or stairs': data.steps || '',
        'Driveway and parking access': data.driveway || '',
        'Access notes': data.accessNotes || '',
        Notes: data.body || '',
        Source: data.source || '',
        'Page URL': data.pageUrl || window.location.href
      };

      if (k && typeof k.push === 'function') {
        k.push(['identify', profile]);
        k.push(['track', 'Requested Quote', props]);
      } else if (k && typeof k.identify === 'function') {
        k.identify(profile);
        k.track('Requested Quote', props);
      }
    } catch (e) {
      /* Never let analytics break the confirmation the customer sees. */
    }
  }

  /* ---------------------------------------------------------
     Submit: validate, stash, then let the browser post natively
     so Shopify's CAPTCHA token rides along.
     --------------------------------------------------------- */
  function wireSubmit(root) {
    var form = root.querySelector('form.coqr__form');
    if (!form || form.dataset.coqrWired === '1') return;
    form.dataset.coqrWired = '1';

    form.addEventListener('submit', function () {
      form.classList.add('is-validated');
      if (!form.checkValidity()) return; // browser shows its own messages

      // Keep the Lead line (first field in the notification email, and all the
      // Gmail preview snippet shows) in step with whatever product was typed.
      var lead = form.querySelector('[data-coqr-lead]');
      if (lead) {
        var prefix = lead.getAttribute('data-coqr-lead-prefix') || 'REQUEST FOR QUOTE';
        var prod = form.querySelector('[name="contact[Product]"]');
        var prodVal = prod ? (prod.value || '').trim() : '';
        lead.value = prodVal ? prefix + ' - ' + prodVal : prefix;
      }

      var payload = readForm(form);
      payload.pageUrl = window.location.href;
      payload.host = root.getAttribute('data-coqr-context') || 'page';
      try {
        // Held only until the redirect lands, then cleared. Contains the
        // customer's own details, which is why it is sessionStorage and
        // why flushPending removes it before doing anything else.
        sessionStorage.setItem(STASH_KEY, JSON.stringify(payload));
      } catch (e) { /* private mode: we simply lose the Klaviyo event */ }

      var btn = form.querySelector('[data-coqr-submit]');
      var label = form.querySelector('[data-coqr-submit-label]');
      var spinner = form.querySelector('.coqr__spinner');
      if (btn) btn.disabled = true;
      if (label) label.textContent = 'Sending';
      if (spinner) spinner.hidden = false;
      // No preventDefault: the native POST is what carries the CAPTCHA token.
    });
  }

  /* ---------------------------------------------------------
     Coming back from Shopify's redirect
     --------------------------------------------------------- */
  function flushPending() {
    if (window.location.search.indexOf('contact_posted=true') === -1) return;

    var raw = null;
    try {
      raw = sessionStorage.getItem(STASH_KEY);
      sessionStorage.removeItem(STASH_KEY);
    } catch (e) { /* nothing to flush */ }

    var data = null;
    if (raw) {
      try { data = JSON.parse(raw); } catch (e) { data = null; }
    }

    // NOT OUR SUBMIT (added 2026-09-18). Shopify marks every contact form on the
    // page as posted, and the ON:PACE product pages carry a second one (the
    // "talk to us" enquiry popup). With no stash, the fallback below would grab
    // this modal's hidden success block and scroll/focus into it. Bail unless
    // there is a stash, or this is the quote page itself, which renders its
    // success block in place and still wants the scroll without sessionStorage.
    if (!data && !document.querySelector('.coqr-page')) return;

    if (data) trackKlaviyo(data);

    // Shopify renders the success block into every form instance on the page,
    // so reopen only the host the customer actually submitted from.
    var host = data && data.host;
    var target = null;

    if (host === 'modal') {
      if (openModal(null)) {
        target = document.querySelector('[data-coqr-modal] [data-coqr-posted]');
      }
    } else if (host === 'rail') {
      var acc = document.querySelector('[data-coqr-acc]');
      if (acc && !acc.classList.contains('is-open')) {
        var toggle = acc.querySelector('[data-coqr-acc-toggle]');
        if (toggle) toggle.click();
      }
      target = acc ? acc.querySelector('[data-coqr-posted]') : null;
    } else {
      target = document.querySelector('.coqr-page [data-coqr-posted]');
    }

    if (!target) target = document.querySelector('[data-coqr-posted]');
    if (target) {
      target.scrollIntoView({ block: 'center', behavior: 'smooth' });
      if (typeof target.focus === 'function') target.focus();
    }
  }

  /* ---------------------------------------------------------
     PDP rail accordion
     --------------------------------------------------------- */
  function wireAccordion(acc) {
    var toggle = acc.querySelector('[data-coqr-acc-toggle]');
    var panel = acc.querySelector('[data-coqr-acc-panel]');
    if (!toggle || !panel) return;

    toggle.addEventListener('click', function () {
      var open = acc.classList.toggle('is-open');
      toggle.setAttribute('aria-expanded', open ? 'true' : 'false');
      panel.hidden = !open;
    });
  }

  /* ---------------------------------------------------------
     Modal
     --------------------------------------------------------- */
  var lastFocused = null;

  function modal() { return document.querySelector('[data-coqr-modal]'); }

  function openModal(trigger) {
    var m = modal();
    if (!m) return false;
    lastFocused = trigger || document.activeElement;
    m.classList.add('is-open');
    m.removeAttribute('aria-hidden');
    document.documentElement.style.overflow = 'hidden';
    document.body.style.overflow = 'hidden';

    var first = m.querySelector('input:not([type="hidden"]):not([disabled]),select,textarea');
    if (first) {
      try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); }
    }
    return true;
  }

  function closeModal() {
    var m = modal();
    if (!m || !m.classList.contains('is-open')) return;
    m.classList.remove('is-open');
    m.setAttribute('aria-hidden', 'true');
    document.documentElement.style.overflow = '';
    document.body.style.overflow = '';
    if (lastFocused && typeof lastFocused.focus === 'function') lastFocused.focus();
    lastFocused = null;
  }

  function trapFocus(ev) {
    var m = modal();
    if (!m || !m.classList.contains('is-open') || ev.key !== 'Tab') return;
    var items = Array.prototype.filter.call(m.querySelectorAll(FOCUSABLE), function (el) {
      return el.offsetParent !== null;
    });
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (ev.shiftKey && document.activeElement === first) {
      ev.preventDefault();
      last.focus();
    } else if (!ev.shiftKey && document.activeElement === last) {
      ev.preventDefault();
      first.focus();
    }
  }

  function isQuoteLink(a) {
    if (!a) return false;
    if (a.hasAttribute('data-coqr-open')) return true;
    var href = a.getAttribute('href') || '';
    // Match the page path, but never a link that is already ON that page,
    // where the form is the page and a modal over it would be silly.
    if (href.indexOf(QUOTE_PATH) === -1) return false;
    return window.location.pathname.indexOf(QUOTE_PATH) === -1;
  }

  /* ---------------------------------------------------------
     Boot
     --------------------------------------------------------- */
  function initForms(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-coqr]'), function (root) {
      if (root.dataset.coqrInit === '1') return;
      root.dataset.coqrInit = '1';
      wireInstall(root);
      wireTouched(root);
      wireSubmit(root);
    });
  }

  function initAccordions(scope) {
    Array.prototype.forEach.call((scope || document).querySelectorAll('[data-coqr-acc]'), function (acc) {
      if (acc.dataset.coqrAccInit === '1') return;
      acc.dataset.coqrAccInit = '1';
      wireAccordion(acc);
    });
  }

  function init() {
    initForms(document);
    initAccordions(document);

    // Delegated so links added later (mega menu, app blocks) still work.
    document.addEventListener('click', function (ev) {
      var a = ev.target.closest ? ev.target.closest('a,button') : null;
      if (!a) return;

      if (a.hasAttribute('data-coqr-close')) {
        ev.preventDefault();
        closeModal();
        return;
      }
      if (isQuoteLink(a)) {
        // If the modal is missing for any reason, let the link fall through
        // to the real page rather than doing nothing.
        if (openModal(a)) ev.preventDefault();
      }
    });

    var m = modal();
    if (m) {
      m.addEventListener('click', function (ev) {
        if (ev.target === m) closeModal();
      });
    }

    document.addEventListener('keydown', function (ev) {
      if (ev.key === 'Escape' || ev.key === 'Esc') closeModal();
      trapFocus(ev);
    });

    flushPending();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor and Section Rendering API re-renders.
  document.addEventListener('shopify:section:load', function (ev) {
    initForms(ev.target);
    initAccordions(ev.target);
  });
})();
